import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';
import {
  usePromotion,
  useCreatePromotion,
  useUpdatePromotion,
} from '@/hooks/usePromotions';
import type {
  PromotionType,
  ScheduleType,
  DiscountType,
  ProductScope,
  CustomerTarget,
  PromotionProductRule,
  RecurringConfig,
  AdvancedConfig,
  AdvancedBuyCondition,
  AdvancedGetReward,
} from '@/hooks/usePromotions';
import { ProductFilterBuilder } from '@/components/promotions/ProductFilterBuilder';
import { PromotionPreview } from '@/components/promotions/PromotionPreview';
import { toast } from 'sonner';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

export default function PromotionForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const { organization } = useAuth();

  // Fetch existing promotion if editing
  const { data: existing, isLoading: loadingExisting } = usePromotion(id || null);

  const createPromotion = useCreatePromotion();
  const updatePromotion = useUpdatePromotion();

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [promotionType, setPromotionType] = useState<PromotionType>('basic');
  const [scheduleType, setScheduleType] = useState<ScheduleType>('one_time');
  const [startDatetime, setStartDatetime] = useState('');
  const [endDatetime, setEndDatetime] = useState('');
  const [noEndDate, setNoEndDate] = useState(true);
  const [discountType, setDiscountType] = useState<DiscountType>('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [productScope, setProductScope] = useState<ProductScope>('all');
  const [customerTarget, setCustomerTarget] = useState<CustomerTarget>('everyone');
  const [allowLoyalty, setAllowLoyalty] = useState(false);
  const [showPosPrompt, setShowPosPrompt] = useState(false);
  const [posPromptMessage, setPosPromptMessage] = useState('');
  const [productRules, setProductRules] = useState<PromotionProductRule[]>([]);
  const [promoCode, setPromoCode] = useState('');
  const [saving, setSaving] = useState(false);

  // Recurring config
  const [recurringDays, setRecurringDays] = useState<number[]>([]);
  const [recurringAllDay, setRecurringAllDay] = useState(true);
  const [recurringStartTime, setRecurringStartTime] = useState('09:00');
  const [recurringEndTime, setRecurringEndTime] = useState('17:00');

  // Advanced config
  const [buyTrigger, setBuyTrigger] = useState<'buys_items' | 'spends_amount'>('buys_items');
  const [buyQuantity, setBuyQuantity] = useState('1');
  const [buyProductScope, setBuyProductScope] = useState<ProductScope>('specific');
  const [buyMinQuantity, setBuyMinQuantity] = useState('');
  const [buyMaxQuantity, setBuyMaxQuantity] = useState('');
  const [showMinMax, setShowMinMax] = useState(false);
  const [buySpendAmount, setBuySpendAmount] = useState('');
  const [getRewardType, setGetRewardType] = useState<'free' | 'percentage' | 'fixed'>('free');
  const [getDiscountValue, setGetDiscountValue] = useState('');
  const [getQuantity, setGetQuantity] = useState('1');
  const [getProductScope, setGetProductScope] = useState<ProductScope>('specific');

  // Fetch locations
  const { data: locations = [] } = useQuery({
    queryKey: ['locations', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data } = await supabase
        .from('locations')
        .select('id, name')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Populate form when editing
  useEffect(() => {
    if (!existing) return;
    const p = existing as any;

    setName(p.name || '');
    setDescription(p.description || '');
    setPromotionType(p.promotion_type || 'basic');
    setScheduleType(p.schedule_type || 'one_time');
    setDiscountType(p.discount_type || 'percentage');
    setDiscountValue(p.discount_value?.toString() || '');
    setProductScope(p.product_scope || 'all');
    setCustomerTarget(p.customer_target || 'everyone');
    setAllowLoyalty(p.allow_loyalty || false);
    setShowPosPrompt(p.show_pos_prompt || false);
    setPosPromptMessage(p.pos_prompt_message || '');

    // Dates
    if (p.start_datetime) {
      setStartDatetime(new Date(p.start_datetime).toISOString().slice(0, 16));
    }
    if (p.end_datetime) {
      setEndDatetime(new Date(p.end_datetime).toISOString().slice(0, 16));
      setNoEndDate(false);
    } else {
      setNoEndDate(true);
    }

    // Locations
    const locs = p.promotion_locations || [];
    setSelectedLocationIds(locs.map((l: any) => l.location_id));

    // Product rules
    setProductRules(p.promotion_product_rules || []);

    // Recurring config
    if (p.recurring_config) {
      const rc = p.recurring_config as RecurringConfig;
      setRecurringDays(rc.days || []);
      setRecurringAllDay(rc.all_day || false);
      setRecurringStartTime(rc.start_time || '09:00');
      setRecurringEndTime(rc.end_time || '17:00');
    }

    // Advanced config
    if (p.advanced_config) {
      const ac = p.advanced_config as AdvancedConfig;
      if (ac.buy_condition) {
        setBuyTrigger(ac.buy_condition.trigger || 'buys_items');
        setBuyQuantity(ac.buy_condition.quantity?.toString() || '1');
        setBuyProductScope(ac.buy_condition.product_scope || 'specific');
        setBuyMinQuantity(ac.buy_condition.min_quantity?.toString() || '');
        setBuyMaxQuantity(ac.buy_condition.max_quantity?.toString() || '');
        setShowMinMax(!!ac.buy_condition.min_quantity || !!ac.buy_condition.max_quantity);
        setBuySpendAmount(ac.buy_condition.spend_amount?.toString() || '');
      }
      if (ac.get_reward) {
        setGetRewardType(ac.get_reward.reward_type || 'free');
        setGetDiscountValue(ac.get_reward.discount_value?.toString() || '');
        setGetQuantity(ac.get_reward.quantity?.toString() || '1');
        setGetProductScope(ac.get_reward.product_scope || 'specific');
      }
    }

    // Promo code (from customer groups)
    const cgs = p.promotion_customer_groups || [];
    const codeGroup = cgs.find((cg: any) => cg.promo_code);
    if (codeGroup) setPromoCode(codeGroup.promo_code || '');
  }, [existing]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Promotion name is required');
      return;
    }
    if (selectedLocationIds.length === 0) {
      toast.error('Please select at least one outlet');
      return;
    }

    setSaving(true);

    try {
      // Build recurring config
      let recurringConfig: RecurringConfig | undefined;
      if (scheduleType === 'recurring') {
        recurringConfig = {
          days: recurringDays,
          all_day: recurringAllDay,
          start_time: recurringAllDay ? undefined : recurringStartTime,
          end_time: recurringAllDay ? undefined : recurringEndTime,
        };
      }

      // Build advanced config
      let advancedConfig: AdvancedConfig | undefined;
      if (promotionType === 'advanced') {
        const buyCondition: AdvancedBuyCondition = {
          trigger: buyTrigger,
          product_scope: buyProductScope,
        };
        if (buyTrigger === 'buys_items') {
          buyCondition.quantity = parseInt(buyQuantity) || 1;
          if (buyMinQuantity) buyCondition.min_quantity = parseInt(buyMinQuantity);
          if (buyMaxQuantity) buyCondition.max_quantity = parseInt(buyMaxQuantity);
        } else {
          buyCondition.spend_amount = parseFloat(buySpendAmount) || 0;
        }

        const getReward: AdvancedGetReward = {
          reward_type: getRewardType,
          quantity: parseInt(getQuantity) || 1,
          product_scope: getProductScope,
        };
        if (getRewardType !== 'free') {
          getReward.discount_value = parseFloat(getDiscountValue) || 0;
        }

        advancedConfig = { buy_condition: buyCondition, get_reward: getReward };
      }

      // Build customer groups
      const customerGroups = customerTarget === 'exclusive' && promoCode
        ? [{ promo_code: promoCode }]
        : [];

      const input = {
        name: name.trim(),
        description: description.trim() || undefined,
        promotion_type: promotionType,
        schedule_type: scheduleType,
        discount_type: promotionType === 'basic' ? discountType : undefined,
        discount_value: promotionType === 'basic' ? parseFloat(discountValue) || 0 : undefined,
        product_scope: promotionType === 'basic' ? productScope : undefined,
        customer_target: customerTarget,
        allow_loyalty: allowLoyalty,
        show_pos_prompt: showPosPrompt,
        pos_prompt_message: showPosPrompt ? posPromptMessage : undefined,
        recurring_config: recurringConfig,
        advanced_config: advancedConfig,
        start_datetime: startDatetime ? new Date(startDatetime).toISOString() : undefined,
        end_datetime: noEndDate ? null : (endDatetime ? new Date(endDatetime).toISOString() : null),
        is_active: true,
        location_ids: selectedLocationIds,
        product_rules: productRules.map(r => ({
          rule_group: r.rule_group,
          filter_type: r.filter_type,
          filter_value: r.filter_value,
        })),
        customer_groups: customerGroups,
      };

      if (isEditing && id) {
        await updatePromotion.mutateAsync({ id, input });
        toast.success('Promotion updated');
      } else {
        await createPromotion.mutateAsync(input);
        toast.success('Promotion created');
      }

      navigate('/products/promotions');
    } catch (err: any) {
      toast.error(`Failed to save promotion: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day: number) => {
    setRecurringDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const toggleLocation = (locId: string) => {
    setSelectedLocationIds(prev =>
      prev.includes(locId) ? prev.filter(id => id !== locId) : [...prev, locId]
    );
  };

  if (isEditing && loadingExisting) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-12">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b -mx-6 px-6 py-4 mb-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/products/promotions')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit promotion' : 'New promotion'}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/products/promotions')}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Save
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-8">
        Set special offers and discounts for your customers.
      </p>

      <div className="space-y-10">
        {/* ---- General ---- */}
        <section className="space-y-4">
          <h3 className="text-lg font-semibold">General</h3>
          <p className="text-sm text-muted-foreground">Name the promotion</p>

          <div className="grid grid-cols-[1fr_300px] gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1.5">Promotion name</label>
                <Input
                  placeholder="Enter promotion name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {name.length}/80 remaining characters
                </p>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1.5">Promotion description</label>
                <Textarea
                  placeholder="Provide a short description to explain this promotion"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-1.5">Outlets</label>
                <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                  {locations.map((loc: any) => (
                    <label key={loc.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={selectedLocationIds.includes(loc.id)}
                        onCheckedChange={() => toggleLocation(loc.id)}
                      />
                      <span className="text-sm">{loc.name}</span>
                    </label>
                  ))}
                  {locations.length === 0 && (
                    <p className="text-sm text-muted-foreground">No outlets available</p>
                  )}
                </div>
                {selectedLocationIds.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedLocationIds.length} outlet{selectedLocationIds.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            </div>

            <PromotionPreview name={name} description={description} />
          </div>
        </section>

        <hr />

        {/* ---- Schedule ---- */}
        <section className="space-y-4">
          <h3 className="text-lg font-semibold">Schedule promotion</h3>
          <p className="text-sm text-muted-foreground">Choose when the promotion happens</p>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className={cn(
                'rounded-lg border-2 p-4 text-left transition-colors',
                scheduleType === 'one_time'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50'
              )}
              onClick={() => setScheduleType('one_time')}
            >
              <p className="font-semibold text-sm">One-time promotion</p>
              <p className="text-xs text-muted-foreground mt-1">
                This promotion happens over a set period of time.
              </p>
            </button>
            <button
              type="button"
              className={cn(
                'rounded-lg border-2 p-4 text-left transition-colors',
                scheduleType === 'recurring'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50'
              )}
              onClick={() => setScheduleType('recurring')}
            >
              <p className="font-semibold text-sm">Recurring promotion</p>
              <p className="text-xs text-muted-foreground mt-1">
                This promotion repeats weekly.
              </p>
            </button>
          </div>

          {scheduleType === 'one_time' && (
            <div className="space-y-3">
              <label className="text-sm font-medium block">Date</label>
              <div className="flex gap-3 items-center">
                <Input
                  type="datetime-local"
                  value={startDatetime}
                  onChange={(e) => setStartDatetime(e.target.value)}
                  className="w-64"
                />
                <span className="text-sm text-muted-foreground">to</span>
                {noEndDate ? (
                  <span className="text-sm text-muted-foreground">No end date</span>
                ) : (
                  <Input
                    type="datetime-local"
                    value={endDatetime}
                    onChange={(e) => setEndDatetime(e.target.value)}
                    className="w-64"
                  />
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={noEndDate}
                  onCheckedChange={(checked) => setNoEndDate(!!checked)}
                />
                <span className="text-sm">No end date</span>
              </label>
            </div>
          )}

          {scheduleType === 'recurring' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-2">Days</label>
                <div className="flex gap-2">
                  {DAYS_OF_WEEK.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      className={cn(
                        'w-12 h-10 rounded-lg border text-sm font-medium transition-colors',
                        recurringDays.includes(day.value)
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border hover:border-muted-foreground/50'
                      )}
                      onClick={() => toggleDay(day.value)}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={recurringAllDay}
                  onCheckedChange={(checked) => setRecurringAllDay(!!checked)}
                />
                <span className="text-sm">All day</span>
              </label>

              {!recurringAllDay && (
                <div className="flex gap-3 items-center">
                  <Input
                    type="time"
                    value={recurringStartTime}
                    onChange={(e) => setRecurringStartTime(e.target.value)}
                    className="w-36"
                  />
                  <span className="text-sm text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={recurringEndTime}
                    onChange={(e) => setRecurringEndTime(e.target.value)}
                    className="w-36"
                  />
                </div>
              )}

              <div className="flex gap-3 items-center">
                <span className="text-sm text-muted-foreground">Promotion active from</span>
                <Input
                  type="datetime-local"
                  value={startDatetime}
                  onChange={(e) => setStartDatetime(e.target.value)}
                  className="w-64"
                />
                <span className="text-sm text-muted-foreground">to</span>
                {noEndDate ? (
                  <span className="text-sm text-muted-foreground">No end date</span>
                ) : (
                  <Input
                    type="datetime-local"
                    value={endDatetime}
                    onChange={(e) => setEndDatetime(e.target.value)}
                    className="w-64"
                  />
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={noEndDate}
                  onCheckedChange={(checked) => setNoEndDate(!!checked)}
                />
                <span className="text-sm">No end date</span>
              </label>
            </div>
          )}
        </section>

        <hr />

        {/* ---- Type of Promotion ---- */}
        <section className="space-y-4">
          <h3 className="text-lg font-semibold">Type of promotion</h3>
          <p className="text-sm text-muted-foreground">Choose a type of promotion to run</p>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className={cn(
                'rounded-lg border-2 p-4 text-left transition-colors',
                promotionType === 'basic'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50'
              )}
              onClick={() => setPromotionType('basic')}
            >
              <p className="font-semibold text-sm">Basic</p>
              <p className="text-xs text-muted-foreground mt-1">
                Offer customers a discount.
              </p>
            </button>
            <button
              type="button"
              className={cn(
                'rounded-lg border-2 p-4 text-left transition-colors',
                promotionType === 'advanced'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50'
              )}
              onClick={() => setPromotionType('advanced')}
            >
              <p className="font-semibold text-sm">Advanced</p>
              <p className="text-xs text-muted-foreground mt-1">
                Offer customers a discount or gift based on what they buy or how much they spend.
              </p>
            </button>
          </div>

          {/* Basic Promotion Config */}
          {promotionType === 'basic' && (
            <div className="space-y-4 bg-muted/30 rounded-lg p-4">
              <div className="flex items-center gap-4">
                <span className="font-semibold text-sm shrink-0">Get</span>

                {/* Discount type toggle */}
                <div className="flex border rounded-md overflow-hidden">
                  <button
                    type="button"
                    className={cn(
                      'px-3 py-1.5 text-sm font-medium transition-colors',
                      discountType === 'percentage' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    )}
                    onClick={() => setDiscountType('percentage')}
                  >
                    %
                  </button>
                  <button
                    type="button"
                    className={cn(
                      'px-3 py-1.5 text-sm font-medium transition-colors border-l',
                      discountType === 'fixed' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    )}
                    onClick={() => setDiscountType('fixed')}
                  >
                    $
                  </button>
                </div>

                <Input
                  type="number"
                  placeholder="0.00"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  className="w-28"
                  min="0"
                  step={discountType === 'percentage' ? '1' : '0.01'}
                />
                <span className="text-sm text-muted-foreground">
                  {discountType === 'percentage' ? '%' : ''}
                </span>

                {/* Product scope toggle */}
                <span className="font-medium text-sm ml-4 shrink-0">Product</span>
                <div className="flex border rounded-md overflow-hidden">
                  <button
                    type="button"
                    className={cn(
                      'px-3 py-1.5 text-sm font-medium transition-colors',
                      productScope === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    )}
                    onClick={() => setProductScope('all')}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={cn(
                      'px-3 py-1.5 text-sm font-medium transition-colors border-l',
                      productScope === 'specific' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    )}
                    onClick={() => setProductScope('specific')}
                  >
                    Specific
                  </button>
                </div>
              </div>

              {productScope === 'specific' && (
                <ProductFilterBuilder
                  ruleGroupPrefix="basic"
                  rules={productRules}
                  onChange={setProductRules}
                />
              )}
            </div>
          )}

          {/* Advanced Promotion Config */}
          {promotionType === 'advanced' && (
            <div className="space-y-6">
              {/* Buy Condition */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h4 className="font-semibold text-sm">When a customer</h4>
                  <Select value={buyTrigger} onValueChange={(v) => setBuyTrigger(v as any)}>
                    <SelectTrigger className="w-56">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buys_items">Buys the following items</SelectItem>
                      <SelectItem value="spends_amount">Spends a certain amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {buyTrigger === 'buys_items' && (
                  <div className="bg-muted/30 rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-4">
                      <span className="font-semibold text-sm">Buy</span>

                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Quantity</label>
                        <Input
                          type="number"
                          value={buyQuantity}
                          onChange={(e) => setBuyQuantity(e.target.value)}
                          className="w-20"
                          min="1"
                        />
                      </div>

                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Product</label>
                        <div className="flex border rounded-md overflow-hidden">
                          <button
                            type="button"
                            className={cn(
                              'px-3 py-1.5 text-sm font-medium transition-colors',
                              buyProductScope === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                            )}
                            onClick={() => setBuyProductScope('all')}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            className={cn(
                              'px-3 py-1.5 text-sm font-medium transition-colors border-l',
                              buyProductScope === 'specific' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                            )}
                            onClick={() => setBuyProductScope('specific')}
                          >
                            Specific
                          </button>
                        </div>
                      </div>
                    </div>

                    {!showMinMax ? (
                      <button
                        type="button"
                        className="text-sm text-primary hover:underline"
                        onClick={() => setShowMinMax(true)}
                      >
                        Set min or max
                      </button>
                    ) : (
                      <div className="flex gap-3 items-center">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Min</label>
                          <Input
                            type="number"
                            placeholder="Min"
                            value={buyMinQuantity}
                            onChange={(e) => setBuyMinQuantity(e.target.value)}
                            className="w-20"
                            min="0"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Max</label>
                          <Input
                            type="number"
                            placeholder="Max"
                            value={buyMaxQuantity}
                            onChange={(e) => setBuyMaxQuantity(e.target.value)}
                            className="w-20"
                            min="0"
                          />
                        </div>
                      </div>
                    )}

                    {buyProductScope === 'specific' && (
                      <ProductFilterBuilder
                        ruleGroupPrefix="buy"
                        rules={productRules}
                        onChange={setProductRules}
                      />
                    )}
                  </div>
                )}

                {buyTrigger === 'spends_amount' && (
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-sm">Spends at least</span>
                      <span className="text-sm">$</span>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={buySpendAmount}
                        onChange={(e) => setBuySpendAmount(e.target.value)}
                        className="w-28"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Get Reward */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h4 className="font-semibold text-sm">Then they will</h4>
                  <span className="text-sm text-muted-foreground">Get the following items</span>
                </div>

                <div className="bg-muted/30 rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-4">
                    <span className="font-semibold text-sm">Get</span>

                    {/* Reward type toggle */}
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Discount</label>
                      <div className="flex border rounded-md overflow-hidden">
                        <button
                          type="button"
                          className={cn(
                            'px-3 py-1.5 text-sm font-medium transition-colors',
                            getRewardType === 'free' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                          )}
                          onClick={() => setGetRewardType('free')}
                        >
                          Free
                        </button>
                        <button
                          type="button"
                          className={cn(
                            'px-3 py-1.5 text-sm font-medium transition-colors border-l',
                            getRewardType === 'percentage' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                          )}
                          onClick={() => setGetRewardType('percentage')}
                        >
                          %
                        </button>
                        <button
                          type="button"
                          className={cn(
                            'px-3 py-1.5 text-sm font-medium transition-colors border-l',
                            getRewardType === 'fixed' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                          )}
                          onClick={() => setGetRewardType('fixed')}
                        >
                          $
                        </button>
                      </div>
                    </div>

                    {getRewardType !== 'free' && (
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Amount</label>
                        <Input
                          type="number"
                          placeholder="0.00"
                          value={getDiscountValue}
                          onChange={(e) => setGetDiscountValue(e.target.value)}
                          className="w-28"
                          min="0"
                          step={getRewardType === 'percentage' ? '1' : '0.01'}
                        />
                      </div>
                    )}

                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Quantity</label>
                      <Input
                        type="number"
                        value={getQuantity}
                        onChange={(e) => setGetQuantity(e.target.value)}
                        className="w-20"
                        min="1"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Product</label>
                      <div className="flex border rounded-md overflow-hidden">
                        <button
                          type="button"
                          className={cn(
                            'px-3 py-1.5 text-sm font-medium transition-colors',
                            getProductScope === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                          )}
                          onClick={() => setGetProductScope('all')}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className={cn(
                            'px-3 py-1.5 text-sm font-medium transition-colors border-l',
                            getProductScope === 'specific' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                          )}
                          onClick={() => setGetProductScope('specific')}
                        >
                          Specific
                        </button>
                      </div>
                    </div>
                  </div>

                  {getProductScope === 'specific' && (
                    <ProductFilterBuilder
                      ruleGroupPrefix="get"
                      rules={productRules}
                      onChange={setProductRules}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </section>

        <hr />

        {/* ---- Show prompt on Sell ---- */}
        {promotionType === 'advanced' && (
          <>
            <section className="space-y-4">
              <h3 className="text-lg font-semibold">Show prompt on Sell</h3>
              <p className="text-sm text-muted-foreground">
                Choose to show a prompt about this promotion on the Sell screen when one of the
                included products is added to the basket.
              </p>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={showPosPrompt}
                  onCheckedChange={(checked) => setShowPosPrompt(!!checked)}
                />
                <span className="text-sm">
                  Show a prompt about this promotion on the Sell screen
                </span>
              </label>
              {showPosPrompt && (
                <Input
                  placeholder="Custom prompt message (optional)"
                  value={posPromptMessage}
                  onChange={(e) => setPosPromptMessage(e.target.value)}
                />
              )}
            </section>
            <hr />
          </>
        )}

        {/* ---- Target Promotion ---- */}
        <section className="space-y-4">
          <h3 className="text-lg font-semibold">Target promotion</h3>
          <p className="text-sm text-muted-foreground">
            Allow all customers to receive this promotion or target specific groups
          </p>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className={cn(
                'rounded-lg border-2 p-4 text-left transition-colors',
                customerTarget === 'everyone'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50'
              )}
              onClick={() => setCustomerTarget('everyone')}
            >
              <p className="font-semibold text-sm">Available to everyone</p>
              <p className="text-xs text-muted-foreground mt-1">
                All customers can receive this promotion.
              </p>
            </button>
            <button
              type="button"
              className={cn(
                'rounded-lg border-2 p-4 text-left transition-colors',
                customerTarget === 'exclusive'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground/50'
              )}
              onClick={() => setCustomerTarget('exclusive')}
            >
              <p className="font-semibold text-sm">Exclusive to some</p>
              <p className="text-xs text-muted-foreground mt-1">
                Only customers within a customer group or with a promo code can redeem this promotion.
              </p>
            </button>
          </div>

          {customerTarget === 'exclusive' && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-1.5">Promo code</label>
                <Input
                  placeholder="Enter promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  className="w-64"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Customers must enter this code to receive the discount.
                </p>
              </div>
            </div>
          )}
        </section>

        <hr />

        {/* ---- Loyalty ---- */}
        <section className="space-y-4">
          <h3 className="text-lg font-semibold">Offer Loyalty with promotion</h3>
          <p className="text-sm text-muted-foreground">
            Choose whether or not you want to offer Loyalty rewards as part of this promotion
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={allowLoyalty}
              onCheckedChange={(checked) => setAllowLoyalty(!!checked)}
            />
            <span className="text-sm">
              Customers can earn Loyalty rewards with this promotion
            </span>
          </label>
        </section>
      </div>
    </div>
  );
}
